import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, LocateFixed, ArrowUpRight, ArrowRight } from "lucide-react";
import { FavouriteButton } from "./FavouriteButton";
import { fetchVolunteers, fetchVolunteerMeta } from "../lib/api";
import { weekendWindow, addDays, iso } from "../lib/dates";
import { safeUrl } from "../lib/safeUrl";

const CATS = "Community Events,Arts & Culture,Sports & Recreation,Food Security,Environment,Community Infrastructure";
const fmt = (d) => new Date(d + "T12:00:00").toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric" });

export const WeekendPicks = () => {
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState("");
  const [geo, setGeo] = useState(null);
  const [state, setState] = useState({ items: null, fallback: false });

  useEffect(() => { fetchVolunteerMeta().then((m) => setCities(m.cities)).catch(() => {}); }, []);
  useEffect(() => {
    const w = weekendWindow();
    const base = { categories: CATS, city: city || undefined, ...(geo || {}), limit: 12 };
    fetchVolunteers({ ...base, date_from: w.from, date_to: w.to }).then((items) => {
      if (items.length) return setState({ items, fallback: false });
      return fetchVolunteers({ ...base, date_from: iso(new Date()), date_to: iso(addDays(new Date(), 21)) }).then((more) => setState({ items: more, fallback: true }));
    }).catch(() => setState({ items: [], fallback: false }));
  }, [city, geo]);

  const nearMe = () => navigator.geolocation?.getCurrentPosition((p) => { setCity(""); setGeo({ lat: p.coords.latitude, lng: p.coords.longitude }); }, () => {});
  const { items, fallback } = state;

  return (
    <section className="mx-auto max-w-7xl px-5 pt-20 sm:px-8" data-testid="weekend-picks">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-amber-700">{fallback ? "Coming up soon" : "This weekend"}</p>
          <h2 className="mt-3 font-bold text-3xl tracking-tight text-slate-900">{fallback ? "Upcoming community events near you" : "This weekend near you"}</h2>
          <p className="mt-2 text-sm text-slate-600">Breakfasts, dances, festivals and local fundraisers that could use an extra pair of hands.</p>
        </div>
        <div className="flex gap-2">
          <select value={city} onChange={(e) => { setGeo(null); setCity(e.target.value); }} className="rounded-full border border-slate-200 bg-white px-3 py-2 text-sm" data-testid="weekend-city-select" aria-label="City">
            <option value="">All of Canada</option>
            {cities.map((c) => <option key={c.city} value={c.city}>{c.city}</option>)}
          </select>
          <button onClick={nearMe} data-testid="weekend-near-me-btn" className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium ${geo ? "bg-emerald-600 text-white" : "bg-sky-900 text-white hover:bg-sky-800"}`}>
            <LocateFixed size={15} /> Near me
          </button>
        </div>
      </div>
      <div className="-mx-5 mt-8 flex snap-x scroll-px-5 gap-4 overflow-x-auto px-5 pb-4 sm:scroll-px-0 sm:mx-0 sm:px-0" data-testid="weekend-strip">
        {items === null && [...Array(4)].map((_, i) => <div key={i} className="h-44 w-72 shrink-0 animate-pulse rounded-3xl bg-white" />)}
        {items?.length === 0 && <p className="text-sm text-slate-500" data-testid="weekend-empty">Nothing listed nearby yet — <Link to="/volunteer" className="text-sky-800 underline">browse all opportunities</Link>.</p>}
        {items?.map((o) => (
          <a key={o.id} href={safeUrl(o.signup_url)} target="_blank" rel="noopener noreferrer" data-testid={`weekend-pick-${o.id}`}
            className="card-rise flex w-72 shrink-0 snap-start flex-col rounded-3xl border border-slate-200 bg-white p-5 hover:shadow-md">
            <span className="flex items-center gap-1.5 text-xs font-medium text-amber-700"><CalendarDays size={14} />{fmt(o.event_date)}{o.event_end_date ? ` – ${fmt(o.event_end_date)}` : ""}<FavouriteButton o={o} className="-my-2 -mr-2 ml-auto" /></span>
            <span className="mt-3 line-clamp-2 font-heading font-semibold leading-snug text-slate-900">{o.title}</span>
            <span className="mt-1 line-clamp-1 text-sm text-sky-800">{o.organization}</span>
            <span className="mt-auto flex items-center justify-between pt-4 text-xs text-slate-500">
              {o.city}, {o.province}{o.distance_km != null ? ` · ${Math.round(o.distance_km)} km` : ""} <ArrowUpRight size={14} />
            </span>
          </a>
        ))}
      </div>
      <Link to="/events" className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-sky-800" data-testid="weekend-browse-all-link">See all community events <ArrowRight size={14} /></Link>
    </section>
  );
};
